import React, { useState, useEffect } from 'react';
import { MaintenanceHistoryEntry } from '../../types/maintenance';

interface MaintenanceHistoryProps {
  history: MaintenanceHistoryEntry[];
  onExportCSV: () => void;
  isLoading?: boolean;
}

export const MaintenanceHistory: React.FC<MaintenanceHistoryProps> = ({
  history,
  onExportCSV,
  isLoading = false,
}) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [machineFilter, setMachineFilter] = useState('');
  const [filteredHistory, setFilteredHistory] = useState<MaintenanceHistoryEntry[]>(history);

  // Filter history based on criteria
  useEffect(() => {
    let filtered = [...history];

    // Filter by date range
    if (startDate) {
      const start = new Date(startDate);
      filtered = filtered.filter(entry => 
        new Date(entry.session.startTime) >= start
      );
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Include the entire end date
      filtered = filtered.filter(entry => 
        new Date(entry.session.startTime) <= end
      );
    }

    // Filter by employee
    if (employeeFilter) {
      const searchTerm = employeeFilter.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.employee.name.toLowerCase().includes(searchTerm) ||
        entry.employee.employeeCode.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by machine
    if (machineFilter) {
      const searchTerm = machineFilter.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.machine.code.toLowerCase().includes(searchTerm) ||
        entry.machine.location.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredHistory(filtered);
  }, [history, startDate, endDate, employeeFilter, machineFilter]);

  // Calculate total maintenance time
  const totalMaintenanceTime = filteredHistory.reduce((total, entry) => {
    return total + (entry.session.duration || 0);
  }, 0);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setEmployeeFilter('');
    setMachineFilter('');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtros</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date Range */}
          <div>
            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
              Data Início
            </label>
            <input
              id="start-date"
              type="date"
              placeholder="Data Início"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
              Data Fim
            </label>
            <input
              id="end-date"
              type="date"
              placeholder="Data Fim"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Employee Filter */}
          <div>
            <label htmlFor="employee-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Funcionário
            </label>
            <input
              id="employee-filter"
              type="text"
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
              placeholder="Nome ou código..."
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Machine Filter */}
          <div>
            <label htmlFor="machine-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Máquina
            </label>
            <input
              id="machine-filter"
              type="text"
              value={machineFilter}
              onChange={(e) => setMachineFilter(e.target.value)}
              placeholder="Código ou local..."
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={clearFilters}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
          >
            Limpar Filtros
          </button>
          
          <button
            onClick={onExportCSV}
            disabled={filteredHistory.length === 0}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-sm"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-sm text-gray-600">Total de Sessões</p>
            <p className="text-3xl font-bold text-orange-600">{filteredHistory.length}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Tempo Total de Manutenção</p>
            <p className="text-3xl font-bold text-orange-600">
              {formatDuration(totalMaintenanceTime)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Duração Média</p>
            <p className="text-3xl font-bold text-orange-600">
              {filteredHistory.length > 0
                ? formatDuration(Math.round(totalMaintenanceTime / filteredHistory.length))
                : '0m'}
            </p>
          </div>
        </div>
      </div>

      {/* History Table */}
      {filteredHistory.length > 0 ? (
        <>
          {/* Mobile Card View */}
          <div className="block lg:hidden space-y-4">
            {filteredHistory.map((entry) => (
              <div
                key={entry.session.id}
                className="bg-white rounded-lg shadow p-4 border border-gray-200"
              >
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-gray-600">Funcionário</p>
                      <p className="font-semibold text-gray-900">{entry.employee.name}</p>
                      <p className="text-xs font-mono text-orange-600">{entry.employee.employeeCode}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Máquina</p>
                      <p className="font-semibold text-gray-900">{entry.machine.code}</p>
                      <p className="text-xs text-gray-500">{entry.machine.location}</p>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Início:</span>
                      <span className="font-medium">{formatDateTime(entry.session.startTime)}</span>
                    </div>
                    {entry.session.endTime && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Fim:</span>
                        <span className="font-medium">{formatDateTime(entry.session.endTime)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Duração:</span>
                      <span className="font-bold text-orange-600">
                        {entry.session.duration ? formatDuration(entry.session.duration) : 'Em andamento'}
                      </span>
                    </div>
                  </div>

                  {entry.session.notes && (
                    <div className="border-t border-gray-200 pt-3">
                      <p className="text-sm text-gray-600 mb-1">Observações:</p>
                      <p className="text-sm text-gray-900">{entry.session.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Código Funcionário
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Funcionário
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Código Máquina
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Local
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Início
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fim
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duração
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredHistory.map((entry) => (
                  <tr key={entry.session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono font-bold text-orange-600">
                        {entry.employee.employeeCode}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {entry.employee.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono font-semibold text-gray-900">
                        {entry.machine.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.machine.location}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTime(entry.session.startTime)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.session.endTime ? formatDateTime(entry.session.endTime) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-bold text-orange-600">
                        {entry.session.duration ? formatDuration(entry.session.duration) : 'Em andamento'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <svg
            className="w-16 h-16 mx-auto text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-gray-500">
            {history.length === 0
              ? 'Nenhum histórico de manutenção disponível.'
              : 'Nenhum resultado encontrado com os filtros aplicados.'}
          </p>
        </div>
      )}
    </div>
  );
};
